import React, { useContext, useEffect, useState } from 'react'
import { generatePerformanceReport } from '../../helpers/generateExcel'
import { AppSettings } from '../../config/app-settings'
import { useGetOpsQuery } from '../../redux/apis/analyticsManagement/aMOpsApiSlice'
import {
  useGetOverallPerformanceQuery
} from '../../redux/apis/usersManagement/users/uMOpsApiSlice'
import ServicesDropdown from '../../components/ServicesDropdown'
import AssigneeDropdown from '../../components/AssigneeDropdown'
import Loader from '../pages/loader'
import PerformanceChart from '../../components/OpsPerformance/PerformanceChart'
import { Card, CardBody, CardHeader } from '../../components/other/card/card'
import { can, getUserId, isOpsUser } from '../../helpers/auth'

const OpsPerformance = React.memo(() => {
  const context = useContext(AppSettings)

  // State for filters
  const [selectedPlatform, setSelectedPlatform] = useState([]) // Array for multi-select
  const opsUserLocked = isOpsUser()
  const [selectedTeamMember, setSelectedTeamMember] = useState(
    opsUserLocked ? [getUserId()] : []
  ) // Array for multi-select
  const [performanceMode, setPerformanceMode] = useState('reviewing') // 'reviewing' or 'insertion'

  // Calculate default dates
  const getDefaultDates = () => {
    const today = new Date()
    const oneMonthAgo = new Date()
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1)

    return {
      from: oneMonthAgo.toISOString().split('T')[0],
      to: today.toISOString().split('T')[0]
    }
  }

  const defaultDates = getDefaultDates()
  const [dateFrom, setDateFrom] = useState(defaultDates.from)
  const [dateTo, setDateTo] = useState(defaultDates.to)

  // Reset all filters to default values
  const handleResetFilters = () => {
    setSelectedPlatform([])
    if (!opsUserLocked) setSelectedTeamMember([])
    const newDefaultDates = getDefaultDates()
    setDateFrom(newDefaultDates.from)
    setDateTo(newDefaultDates.to)
  }

  // Fetch data from analytics API
  const { data: opsData, isLoading: opsLoading } = useGetOpsQuery({})

  // Fetch data from the new unified API endpoint
  const { data: performanceData, isLoading: performanceLoading } =
    useGetOverallPerformanceQuery({
      start_date: dateFrom,
      end_date: dateTo,
      ...(selectedPlatform?.length > 0 && { service_id: JSON.stringify(selectedPlatform) }),
      ...(selectedTeamMember?.length > 0 && { user_id: JSON.stringify(selectedTeamMember) })
    })

  useEffect(() => {
    context.setAppHeaderNone(false)
    context.setAppSidebarNone(false)
    context.setAppContentClass('')
    context.setAppThemePanal(true)

    return () => {
      context.setAppHeaderNone(true)
      context.setAppSidebarNone(true)
      context.setAppContentClass('p-0')
      context.setAppThemePanal(false)
    }
  }, [context])

  // Calculate total links and prepare chart data
  const { totalLinks, totalPct, platformData } = React.useMemo(() => {
    if (!performanceData?.data) {
      return { totalLinks: 0, totalPct: 0, platformData: [] }
    }

    let total = 0
    let totalPct = 0
    const platformTotals = {}
    const metric = performanceMode === 'reviewing' ? 'reviewed' : 'inserted'
    const metricPct = performanceMode === 'reviewing' ? 'reviewed_pct' : 'inserted_pct'

    // Process data from the new API structure
    const data = performanceData.data

    // Process monthly_data (per user breakdown)
    if (data.monthly_data) {
      Object.entries(data.monthly_data).forEach(([userName, userServices]) => {
        // Sum overall totals from users that have the 'overall' key
        if (userServices.overall) {
          total += userServices.overall[metric] || 0
          totalPct += userServices.overall[metricPct] || 0
        }

        Object.entries(userServices).forEach(([serviceName, stats]) => {
          // Skip the 'overall' key at user level
          if (serviceName !== 'overall') {
            if (!platformTotals[serviceName]) {
              platformTotals[serviceName] = { value: 0, teamMembers: {} }
            }
            const memberValue = stats[metric] || 0
            const memberPct = stats[metricPct] || 0
            if (!platformTotals[serviceName].teamMembers[userName]) {
              platformTotals[serviceName].teamMembers[userName] = { count: 0, pct: 0 }
            }
            platformTotals[serviceName].teamMembers[userName].count += memberValue
            platformTotals[serviceName].teamMembers[userName].pct += memberPct
            platformTotals[serviceName].value += memberValue
          }
        })
      })
    }

    const platformDataArray = Object.entries(platformTotals)
      .map(([platform, data]) => ({
        platform,
        total: data.value,
        teamMembers: data.teamMembers
      }))
      .filter(item => item.total > 0) // Only show platforms with data
      .sort((a, b) => b.total - a.total)

    return {
      totalLinks: total,
      totalPct,
      platformData: platformDataArray
    }
  }, [performanceData, performanceMode])

  // Console log all the data
  useEffect(() => {
    console.log('=== Analytics Management API ===')
    console.log('Ops Data:', opsData)

    console.log('\n=== Users Management API ===')
    console.log('Performance Data:', performanceData)

    console.log('\n=== Calculated Data ===')
    console.log('Total Links:', totalLinks)
    console.log('Platform Data:', platformData)
  }, [opsData, performanceData, totalLinks, platformData])

  const handleDownloadReport = () => {
    if (!performanceData?.data?.monthly_data) return
    generatePerformanceReport(performanceData.data.monthly_data, dateFrom, dateTo, opsData?.data)
  }

  const isLoading = opsLoading || performanceLoading

  return (
    <>
      <Loader isLoading={isLoading} />
      <div className='container-fluid'>
        {/* Performance Mode Switch */}
        <div className='row mb-4'>
          <div className='col-12'>
            <Card>
              <CardHeader>
                <div className='d-flex align-items-center justify-content-between'>
                  <ul className='nav nav-tabs card-header-tabs flex-grow-1 border-0'>
                    {can('view_Reviewing_Performance_ops') && (
                      <li className='nav-item'>
                        <a
                          className={`nav-link ${
                            performanceMode === 'reviewing' ? 'active' : ''
                          }`}
                          href='#/'
                          onClick={e => {
                            e.preventDefault()
                            setPerformanceMode('reviewing')
                          }}
                        >
                          Reviewing Performance
                        </a>
                      </li>
                    )}
                    {can('view_Insertion_Performance_ops') && (
                      <li className='nav-item'>
                        <a
                          className={`nav-link ${
                            performanceMode === 'insertion' ? 'active' : ''
                          }`}
                          href='#/'
                          onClick={e => {
                            e.preventDefault()
                            setPerformanceMode('insertion')
                          }}
                        >
                          Insertion Performance
                        </a>
                      </li>
                    )}
                  </ul>
                  <button
                    type='button'
                    className='btn btn-theme btn-sm ms-3 flex-shrink-0'
                    onClick={handleDownloadReport}
                    disabled={!performanceData?.data?.monthly_data}
                  >
                    <i className='fas fa-download me-2'></i>
                    {opsUserLocked ? 'Download My Report' : 'Download Report'}
                  </button>
                </div>
              </CardHeader>
              <CardBody>
                {/* KPI Section */}
                <div className='row mb-4'>
                  <div className='col-12 text-center'>
                    <h1 className='display-3 fw-bold mb-2'>
                      {totalLinks.toLocaleString()}
                    </h1>
                    <p className='text-muted fs-5 mb-1'>
                      total of{' '}
                      {performanceMode === 'reviewing'
                        ? 'reviewed'
                        : 'inserted'}{' '}
                      links
                    </p>
                    {totalPct > 0 && selectedTeamMember.length > 0 && (
                      <p className='text-muted fs-6'>
                        Overall:{' '}
                        <span className='fw-semibold text-theme'>
                          {totalPct.toFixed(2)}%
                        </span>
                      </p>
                    )}
                  </div>
                </div>

                {/* Filters Section */}
                <div className='row mb-4'>
                  <div className='col-md-6'>
                    <ServicesDropdown
                      value={selectedPlatform}
                      onChange={value => setSelectedPlatform(value)}
                      mode="multiple"
                      className='customSelect'
                      is_collector={true}
                    />
                  </div>
                  <div className='col-md-6'>
                    <AssigneeDropdown
                      value={selectedTeamMember}
                      onChange={value => setSelectedTeamMember(value)}
                      mode='multiple'
                      className='customSelect'
                      disabled={opsUserLocked}
                    />
                  </div>
                </div>

                <div className='row mb-4'>
                  <div className='col-md-6'>
                    <label className='form-label '>Date From</label>
                    <input
                      type='date'
                      className='form-control'
                      value={dateFrom}
                      onChange={e => setDateFrom(e.target.value)}
                    />
                  </div>
                  <div className='col-md-6'>
                    <label className='form-label'>Date To</label>
                    <input
                      type='date'
                      className='form-control'
                      value={dateTo}
                      onChange={e => setDateTo(e.target.value)}
                    />
                  </div>
                </div>

                {/* Reset Button */}
                <div className='row mb-4'>
                  <div className='col-12'>
                    <button
                      type='button'
                      className='btn btn-secondary'
                      onClick={handleResetFilters}
                    >
                      Reset Filters
                    </button>
                  </div>
                </div>

                {/* Chart Section */}
                <div className='row'>
                  <div className='col-12'>
                    <PerformanceChart
                      platformData={platformData}
                      selectedPlatform={selectedPlatform}
                      selectedTeamMember={selectedTeamMember}
                    />
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
    </>
  )
})

export default OpsPerformance
